import uniqid from "uniqid";
import api from "../services/api";

function getFormattedDate(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Mês começa de 0 (janeiro) a 11 (dezembro)
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function formatDateAndTime(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const time = formatTime(date);
  return `${day}/${month}/${year} - ${time}`;
}

function formatTime(date: Date): string {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/**
 * Converte uma data para o timezone de São Paulo, Brasil
 * Garante que a data seja exibida corretamente no horário de São Paulo
 * @param dateString - String de data (ISO ou qualquer formato aceito pelo Date)
 * @returns Date ajustada para exibir no timezone de São Paulo
 */
function toSaoPauloTimeZone(dateString: string | Date): Date {
  const date =
    typeof dateString === "string" ? new Date(dateString) : dateString;

  // Usar Intl.DateTimeFormat para obter os valores no timezone de São Paulo
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  // Obter as partes da data formatada no timezone de São Paulo
  const parts = formatter.formatToParts(date);

  // Extrair os valores
  const year = parseInt(parts.find((p) => p.type === "year")?.value || "0");
  const month =
    parseInt(parts.find((p) => p.type === "month")?.value || "1") - 1; // Mês é 0-indexed
  const day = parseInt(parts.find((p) => p.type === "day")?.value || "1");
  const hour = parseInt(parts.find((p) => p.type === "hour")?.value || "0");
  const minute = parseInt(parts.find((p) => p.type === "minute")?.value || "0");
  const second = parseInt(parts.find((p) => p.type === "second")?.value || "0");

  // Criar uma nova data no timezone local com os valores de São Paulo
  // Isso garante que getHours() e getMinutes() retornem os valores corretos de São Paulo
  return new Date(year, month, day, hour, minute, second);
}

function formatDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function addArray<T>(array: T[], defaultValue: T): T[] {
  const lengthDiff = 6 - array.length;
  if (lengthDiff > 0) {
    const filledArray = Array.from({ length: lengthDiff }, () => defaultValue);
    return filledArray.concat(array);
  }
  return array;
}

function lastSixMonths(): string[] {
  const monthsAbbr = [
    "Jan",
    "Fev",
    "Mar",
    "Abr",
    "Mai",
    "Jun",
    "Jul",
    "Ago",
    "Set",
    "Out",
    "Nov",
    "Dez",
  ];

  const currentMonth = new Date().getMonth();

  // Cria um array com os índices dos últimos seis meses, incluindo o mês atual
  const lastSixMonthsIndices = Array.from(
    { length: 6 },
    (_, i) => (currentMonth - i + 12) % 12,
  );

  // Mapeia os índices dos meses para suas abreviações correspondentes
  const lastSixMonthsAbbr = lastSixMonthsIndices.map(
    (index) => monthsAbbr[index],
  );

  return lastSixMonthsAbbr;
}

function isMobile(): boolean {
  const mobileWidthThreshold = 768;

  return window.innerWidth < mobileWidthThreshold;
}

/**
 * Corrige URLs pré-assinadas S3: o '+' na assinatura deve ser '%2B' na URL,
 * senão é interpretado como espaço e a S3 retorna SignatureDoesNotMatch.
 */
function fixS3PresignedUrl(url: string): string {
  if (!url || !url.includes("Signature=")) return url;
  return url.replace(
    /(Signature=)([^&]*)/,
    (_, prefix, sig) => prefix + sig.replace(/\+/g, "%2B"),
  );
}

export type GetUriImageOptions = {
  width?: number;
  height?: number;
};

function getUriImage(
  myValue?: string | null,
  user_id = "",
  options?: GetUriImageOptions,
): string {
  if (myValue === "" || !myValue) {
    return "https://upload.wikimedia.org/wikipedia/commons/7/70/Solid_white.svg";
  }
  if (myValue?.indexOf(";base64,") !== -1) {
    return myValue;
  }

  if (myValue.indexOf("static/media") !== -1) {
    return myValue;
  }

  let url: string;
  if (user_id !== "") {
    url =
      myValue.indexOf("http") !== -1
        ? myValue
        : `${api.getUri()}/images/download/${user_id}/${encodeURIComponent(myValue)}`;
  } else {
    url =
      myValue.indexOf("http") !== -1
        ? myValue
        : myValue.includes("/")
          ? `${api.getUri()}/images/download/${encodeURIComponent(myValue)}`
          : `${api.getUri()}/images/download-public/${encodeURIComponent(myValue)}`;
  }

  if (options?.width != null || options?.height != null) {
    const params = new URLSearchParams();
    if (options.width != null) params.set("w", String(options.width));
    if (options.height != null) params.set("h", String(options.height));
    url += (url.includes("?") ? "&" : "?") + params.toString();
  }

  return fixS3PresignedUrl(url);
}

function calcularIdade(dataNascimento: Date): number {
  const hoje: Date = new Date();
  const anoAtual: number = hoje.getFullYear();
  const anoNascimento: number = dataNascimento.getFullYear();
  const idade: number = anoAtual - anoNascimento;

  const mesAtual: number = hoje.getMonth() + 1;
  const diaAtual: number = hoje.getDate();
  const mesNascimento: number = dataNascimento.getMonth() + 1;
  const diaNascimento: number = dataNascimento.getDate();

  if (
    mesAtual < mesNascimento ||
    (mesAtual === mesNascimento && diaAtual < diaNascimento)
  ) {
    return idade - 1;
  }

  return idade;
}

function formatarTelefone(numero: string): string {
  const apenasDigitos = numero.replace(/\D/g, "");

  let prefixo = "";
  if (apenasDigitos.length > 11) {
    prefixo = `+${apenasDigitos.substring(0, apenasDigitos.length - 11)}`;
  }

  const regexPadrao = /^(?:\d{2})?(\d{2})(\d{4,5})(\d{4})$/;
  const partes = apenasDigitos.match(regexPadrao);

  if (partes) {
    return `${prefixo} (${partes[1]}) ${partes[2]}-${partes[3]}`;
  } else {
    return numero;
  }
}

function removerAcentos(s: string): string {
  if (!s) return "";
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function generateId(ids = ""): string {
  return uniqid(ids) + uniqid();
}

function formatPhoneNumber(phoneNumber: string): string | null {
  const cleaned = phoneNumber.replace(/\D/g, "");

  const isValidNumber = /^[0-9]{10}$/g.test(cleaned);

  if (!isValidNumber) {
    return null;
  }

  const formatted = `(${cleaned.slice(0, 2)}) ${cleaned.slice(
    2,
    6,
  )}-${cleaned.slice(6)}`;

  return formatted;
}

function toLocalISOString(date: Date | number): string {
  const d = new Date(date);
  const tzOffset = d.getTimezoneOffset() * 60000; // Offset in milliseconds
  const localISOTime = new Date(d.getTime() - tzOffset)
    .toISOString()
    .slice(0, 16);
  return localISOTime;
}

/**
 * Converte uma data para o formato ISO string no timezone de São Paulo
 * Usado para enviar datas para a API que espera horários em São Paulo
 * @param date - Date object
 * @returns String no formato "YYYY-MM-DDTHH:MM" no timezone de São Paulo
 */
function toSaoPauloISOString(date: Date | number): string {
  const d = typeof date === "number" ? new Date(date) : date;

  // Usar Intl.DateTimeFormat para obter os valores no timezone de São Paulo
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  // Obter as partes da data formatada no timezone de São Paulo
  const parts = formatter.formatToParts(d);

  // Extrair os valores
  const year = parts.find((p) => p.type === "year")?.value || "0";
  const month = parts.find((p) => p.type === "month")?.value || "01";
  const day = parts.find((p) => p.type === "day")?.value || "01";
  const hour = parts.find((p) => p.type === "hour")?.value || "00";
  const minute = parts.find((p) => p.type === "minute")?.value || "00";

  // Retornar no formato "YYYY-MM-DDTHH:MM"
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function truncateString(str: string, maxLength: number): string {
  if (str.length > maxLength) {
    return str.slice(0, maxLength - 3) + "...";
  }
  return str;
}

function formatCurrency(value: number): string {
  try {
    const formattedValue = value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });

    return formattedValue;
  } catch (e) {
    return "";
  }
}

function genCode(str: string, multiplicar?: number): string {
  const numerosEncontrados = str.match(/\d+/g);

  if (!numerosEncontrados) {
    return "0000";
  }

  let codigo = numerosEncontrados.slice(0, 4).join("").slice(0, 4);

  if (multiplicar) {
    if (multiplicar === 1) {
      multiplicar = 2;
    }
    codigo = (parseInt(codigo, 10) * multiplicar).toString().slice(0, 4);
  }

  return codigo;
}

const utils = {
  getFormattedDate,
  getUriImage,
  formatDateAndTime,
  calcularIdade,
  truncateString,
  isMobile,
  removerAcentos,
  formatTime,
  addArray,
  generateId,
  formatPhoneNumber,
  toLocalISOString,
  toSaoPauloISOString,
  formatarTelefone,
  formatDate,
  lastSixMonths,
  formatCurrency,
  genCode,
  toSaoPauloTimeZone,
};

export default utils;
